import React, { useState, useEffect, useCallback } from 'react';
import './StudentDocumentsModal.css';
import collegeConfig from '../../config/collegeConfig';
import { saveFile } from '../../utils/saveFile';
import { generateClassicDRPCertificate } from '../../utils/classicDrpCertificateGenerator';
import AttachmentItem from './AttachmentItem';
import {
  FaTimes, FaSpinner, FaCertificate, FaNewspaper, FaHospital,
  FaDownload, FaCheckCircle, FaHistory
} from 'react-icons/fa';

const API = collegeConfig.api.baseURL;

const mimeToType = (mimeType) => (mimeType && mimeType.startsWith('image/')) ? 'image' : 'pdf';

// Records saved before the subType field existed have subType = null — treat as 'Publication'.
const SUBTYPE_LABELS = { paper: 'Paper', poster: 'Poster', publication: 'Publication' };
const subTypeLabel = (subType) => SUBTYPE_LABELS[subType] || 'Publication';

// Renders a single document row: an AttachmentItem for B2-stored files, a plain
// download button for legacy inline-base64 files, or a "not uploaded" placeholder.
function DocRow({ label, doc, extra }) {
  const [saving, setSaving] = useState(false);

  const handleLegacyDownload = async () => {
    setSaving(true);
    try {
      await saveFile(doc.data, doc.mimeType || 'application/pdf', doc.fileName || `${label}.pdf`);
    } catch (_) { /* ignore */ }
    setSaving(false);
  };

  return (
    <div className="sdm-row">
      <div className="sdm-row-main">
        <span className="sdm-row-label">{label}</span>
        {extra && <span className="sdm-row-extra">{extra}</span>}
      </div>
      {doc.fileKey ? (
        <AttachmentItem attachment={{ key: doc.fileKey, name: doc.fileName || label, type: mimeToType(doc.mimeType) }} />
      ) : doc.data ? (
        <button className="sdm-download-btn" onClick={handleLegacyDownload} disabled={saving}>
          {saving ? <FaSpinner className="sdm-spin" /> : <FaDownload />} Download
        </button>
      ) : (
        <span className="sdm-not-uploaded">Not uploaded</span>
      )}
    </div>
  );
}

// System-generated (client-side jsPDF) DRP certificate — no stored file, regenerated on demand.
function SystemCertRow({ label, drpCert, studentName, courseName }) {
  const [saving, setSaving] = useState(false);

  const handleGenerate = async () => {
    setSaving(true);
    try {
      await generateClassicDRPCertificate(
        {
          studentName,
          courseName,
          hospitalName: drpCert.hospitalName,
          fromDate: drpCert.fromDate,
          toDate: drpCert.toDate,
          inchargeName: drpCert.inchargeName
        },
        collegeConfig.college?.name || collegeConfig.shortName,
        collegeConfig.logo
      );
    } catch (_) { /* ignore */ }
    setSaving(false);
  };

  return (
    <div className="sdm-row">
      <div className="sdm-row-main">
        <span className="sdm-row-label">{label}</span>
        <span className="sdm-row-extra">{drpCert.hospitalName || '—'}</span>
      </div>
      <button className="sdm-download-btn" onClick={handleGenerate} disabled={saving}>
        {saving ? <FaSpinner className="sdm-spin" /> : <FaDownload />} Download
      </button>
    </div>
  );
}

const StudentDocumentsModal = ({ studentId, courseId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const params = courseId ? `?courseId=${courseId}` : '';
      const res = await fetch(`${API}/exam-eligibility/documents/${studentId}${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await res.json();
      if (d.success) setData(d);
      else setError(d.message || 'Failed to load documents');
    } catch (_) {
      setError('Failed to load documents');
    }
    setLoading(false);
  }, [studentId, courseId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const docs = data?.documents;

  return (
    <div className="sdm-overlay" onClick={onClose}>
      <div className="sdm-content" onClick={e => e.stopPropagation()}>
        <div className="sdm-header">
          <div>
            <h2 className="sdm-title">Supporting Documents</h2>
            {data?.student && (
              <p className="sdm-subtitle">
                {data.student.name}{data.student.registrationNumber ? ` · ${data.student.registrationNumber}` : ''}
              </p>
            )}
          </div>
          <button className="sdm-close" onClick={onClose}><FaTimes /></button>
        </div>

        <div className="sdm-body">
          {loading ? (
            <div className="sdm-centered"><FaSpinner className="sdm-spin sdm-spin-lg" /></div>
          ) : error ? (
            <p className="sdm-error">{error}</p>
          ) : (
            <>
              {/* Certifications */}
              <div className="sdm-section">
                <div className="sdm-section-title"><FaCertificate /> Certificates</div>
                <div className="sdm-rows">
                  {docs.certifications.map(c => (
                    <DocRow key={c.type} label={c.label} doc={c} />
                  ))}
                </div>
              </div>

              {/* Paper / Poster / Publication & Awards */}
              <div className="sdm-section">
                <div className="sdm-section-title"><FaNewspaper /> Paper / Poster / Publication &amp; Awards</div>
                {docs.achievements.length === 0 ? (
                  <p className="sdm-empty">No papers, posters, publications, or awards recorded.</p>
                ) : (
                  <div className="sdm-rows">
                    {docs.achievements.map(a => (
                      <DocRow
                        key={a._id}
                        label={a.title}
                        doc={a}
                        extra={a.type === 'award' ? 'Award' : subTypeLabel(a.subType)}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* DRP Certificate */}
              <div className="sdm-section">
                <div className="sdm-section-title"><FaHospital /> DRP (Rural Posting) Certificate</div>
                {docs.drp.mode === 'advanced' ? (
                  <p className="sdm-empty">
                    {docs.drp.completedPostings > 0
                      ? <><FaCheckCircle style={{ color: '#27AE60', marginRight: 6 }} />{docs.drp.completedPostings} completed posting(s) verified — advanced DRP mode has no single certificate file.</>
                      : 'No completed DRP postings yet.'}
                  </p>
                ) : (
                  <>
                    <div className="sdm-rows">
                      {!docs.drp.current ? (
                        <p className="sdm-empty">No DRP posting recorded.</p>
                      ) : docs.drp.current.uploadedCertificate.uploaded ? (
                        <DocRow
                          label="Current Posting Certificate (Uploaded)"
                          doc={docs.drp.current.uploadedCertificate}
                          extra={docs.drp.current.hospitalName}
                        />
                      ) : docs.drp.current.systemIssued ? (
                        <SystemCertRow
                          label="Current Posting Certificate (System)"
                          drpCert={docs.drp.current}
                          studentName={data.student.name}
                          courseName={data.courseName}
                        />
                      ) : (
                        <div className="sdm-row">
                          <div className="sdm-row-main">
                            <span className="sdm-row-label">Current Posting Certificate</span>
                            <span className="sdm-row-extra">{docs.drp.current.hospitalName || '—'}</span>
                          </div>
                          <span className="sdm-not-uploaded">Not issued yet</span>
                        </div>
                      )}
                    </div>

                    {docs.drp.history.length > 0 && (
                      <div className="sdm-history">
                        <div className="sdm-history-title"><FaHistory /> Past Postings</div>
                        <div className="sdm-rows">
                          {docs.drp.history.map((h, i) => (
                            h.uploadedCertificate.uploaded ? (
                              <DocRow key={i} label={`Past Posting — ${h.hospitalName || '—'}`} doc={h.uploadedCertificate} />
                            ) : h.systemIssued ? (
                              <SystemCertRow
                                key={i}
                                label={`Past Posting — ${h.hospitalName || '—'}`}
                                drpCert={h}
                                studentName={data.student.name}
                                courseName={data.courseName}
                              />
                            ) : (
                              <div className="sdm-row" key={i}>
                                <div className="sdm-row-main">
                                  <span className="sdm-row-label">Past Posting — {h.hospitalName || '—'}</span>
                                </div>
                                <span className="sdm-not-uploaded">No certificate</span>
                              </div>
                            )
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentDocumentsModal;
